import React, { useContext, useEffect } from "react";
import { FieldArray } from "formik";
import { Row, Col, Label, Button, Card, CardBody } from "reactstrap";
import InputField from "../../Utils/InputField";
import SelectField from "../../Utils/SelectField";
import { handleChangeForImsi } from "../../Customers/constants/HandleChangeFunctions";
import { CreateOfferContext } from "./context";
import { LabelText } from "../../Utils/Input";
import { Link } from "react-router-dom";

const AttributeBasedDetailsTableRecurring = ({
  tableDataRecurring,
  setTableDataRecurring,
  validateCurrentAttributeDetails,
  addAttributeBasedDetail,
}) => {
  const {
    index,
    handleChange,
    isSubscribed,
    values,
    setFieldTouched,
    setTouched,
    setFieldValue,
    errors,
    touched,
    isSubmitting,
    attributes,
  } = useContext(CreateOfferContext);
  // Options for logical operator
  const logicalOperatorOptions = [
    { value: "AND", label: "AND" },
    { value: "OR", label: "OR" },
    { value: "NOT", label: "NOT" },
  ];

  // Filter attributes similar to the main component
  const filteredAttributes =
    attributes?.filter(
      (attr) =>
        (attr.type === "customer" || attr.type === "usage") &&
        attr.valueType !== "list"
    ) || [];

  // Create options for attribute name select field
  const attributeNameOptions = filteredAttributes.map((attr) => ({
    value: attr.name,
    label: attr.name,
  }));

  // Options for condition operator
  const conditionOperatorOptions = [
    { value: "EQUALS", label: "Equals" },
    { value: "NOT_EQUALS", label: "Not Equals" },
    { value: "GREATER_THAN", label: "Greater Than" },
    { value: "LESS_THAN", label: "Less Than" },
    { value: "GREATER_THAN_OR_EQUAL", label: "Greater Than or Equal" },
    { value: "LESS_THAN_OR_EQUAL", label: "Less Than or Equal" },
  ];

  // Initialize with one empty attribute detail set if no data exists
  useEffect(() => {
    if (tableDataRecurring.length === 0) {
      const initialDetail = {
        ruleId: "",
        name: "",
        logicalOperator: "",
        conditions: [
          {
            attributeName: "",
            operator: "",
            value: "",
          },
        ],
      };

      const initialData = [initialDetail];
      setTableDataRecurring(initialData);
      setFieldValue(
        `recurringUsageRatePlan[${index}].attributeBasedDetails`,
        initialData
      );
    }
  }, [tableDataRecurring, setTableDataRecurring, setFieldValue, index]);

  const removeAttributeBasedDetail = (detailIndex) => {
    // Prevent removing if it's the last item
    if (tableDataRecurring.length <= 1) {
      return;
    }

    const updatedData = tableDataRecurring.filter((_, i) => i !== detailIndex);
    setTableDataRecurring(updatedData);
    setFieldValue(
      `recurringUsageRatePlan[${index}].attributeBasedDetails`,
      updatedData
    );
  };

  const addCondition = (detailIndex) => {
    // Validate current conditions before adding new one
    const currentDetail = tableDataRecurring[detailIndex];
    const hasInvalidConditions = currentDetail.conditions?.some(
      (condition) =>
        !condition.attributeName || !condition.operator || !condition.value
    );

    if (hasInvalidConditions) {
      // Mark conditions as touched for validation display
      const newTouched = { ...touched };
      if (!newTouched.recurringUsageRatePlan)
        newTouched.recurringUsageRatePlan = {};
      if (!newTouched.recurringUsageRatePlan[index])
        newTouched.recurringUsageRatePlan[index] = {};
      if (!newTouched.recurringUsageRatePlan[index].attributeBasedDetails) {
        newTouched.recurringUsageRatePlan[index].attributeBasedDetails = [];
      }
      if (
        !newTouched.recurringUsageRatePlan[index].attributeBasedDetails[
          detailIndex
        ]
      ) {
        newTouched.recurringUsageRatePlan[index].attributeBasedDetails[
          detailIndex
        ] = {};
      }

      newTouched.recurringUsageRatePlan[index].attributeBasedDetails[
        detailIndex
      ].conditions =
        currentDetail.conditions?.map(() => ({
          attributeName: true,
          operator: true,
          value: true,
        })) || [];

      setTouched(newTouched);
      return;
    }

    const updatedData = [...tableDataRecurring];
    updatedData[detailIndex].conditions.push({
      attributeName: "",
      operator: "",
      value: "",
    });
    setTableDataRecurring(updatedData);
    setFieldValue(
      `recurringUsageRatePlan[${index}].attributeBasedDetails`,
      updatedData
    );
  };

  const removeCondition = (detailIndex, conditionIndex) => {
    const updatedData = [...tableDataRecurring];
    updatedData[detailIndex].conditions = updatedData[
      detailIndex
    ].conditions.filter((_, i) => i !== conditionIndex);
    setTableDataRecurring(updatedData);
    setFieldValue(
      `recurringUsageRatePlan[${index}].attributeBasedDetails`,
      updatedData
    );
  };

  const updateField = (detailIndex, fieldName, value) => {
    const updatedData = [...tableDataRecurring];
    updatedData[detailIndex][fieldName] = value;
    setTableDataRecurring(updatedData);
    setFieldValue(
      `recurringUsageRatePlan[${index}].attributeBasedDetails`,
      updatedData
    );

    // Set field as touched
    setFieldTouched(
      `recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].${fieldName}`,
      true
    );
  };

  const updateConditionField = (
    detailIndex,
    conditionIndex,
    fieldName,
    value
  ) => {
    const updatedData = [...tableDataRecurring];
    updatedData[detailIndex].conditions[conditionIndex][fieldName] = value;

    // If attribute name is being changed, automatically set the default value
    if (fieldName === "attributeName") {
      // The value parameter itself contains the default value in value.value
      const selectedAttribute = filteredAttributes.find(
        (attr) => attr.name === value.value
      );
      if (selectedAttribute && selectedAttribute.default) {
        updatedData[detailIndex].conditions[conditionIndex]["value"] =
          selectedAttribute.default;
      }
    }

    // Update state first
    setTableDataRecurring(updatedData);

    // Then update Formik values
    setFieldValue(
      `recurringUsageRatePlan[${index}].attributeBasedDetails`,
      updatedData
    );

    // Set field as touched
    setFieldTouched(
      `recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].conditions[${conditionIndex}].${fieldName}`,
      true
    );

    // Also set value field as touched if attribute name changed
    if (fieldName === "attributeName") {
      setFieldTouched(
        `recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].conditions[${conditionIndex}].value`,
        true
      );
    }
  };

  // Helper function to get field error
  const getFieldError = (detailIndex, fieldName) => {
    return errors.recurringUsageRatePlan?.[index]?.attributeBasedDetails?.[
      detailIndex
    ]?.[fieldName];
  };

  // Helper function to check if field is touched
  const isFieldTouched = (detailIndex, fieldName) => {
    return touched.recurringUsageRatePlan?.[index]?.attributeBasedDetails?.[
      detailIndex
    ]?.[fieldName];
  };

  // Helper function to get condition field error
  const getConditionFieldError = (detailIndex, conditionIndex, fieldName) => {
    return errors.recurringUsageRatePlan?.[index]?.attributeBasedDetails?.[
      detailIndex
    ]?.conditions?.[conditionIndex]?.[fieldName];
  };

  // Helper function to check if condition field is touched
  const isConditionFieldTouched = (detailIndex, conditionIndex, fieldName) => {
    return touched.recurringUsageRatePlan?.[index]?.attributeBasedDetails?.[
      detailIndex
    ]?.conditions?.[conditionIndex]?.[fieldName];
  };

  return (
    <>
      {tableDataRecurring.map((detail, detailIndex) => (
        <div key={detailIndex} className="border-top mb-3 pt-3 px-1">
          <Row className="px-3">
            <Col xs={12} md={6} lg={4} xxl={3} className="mb-3">
              <Label>Rule ID</Label>
              <LabelText
                htmlFor={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].ruleId`}
                classLabelName={`d-block ms-0 rv-sub-label ${
                  isFieldTouched(detailIndex, "ruleId") && !detail.ruleId
                    ? "rv-label-danger"
                    : "rv-label-muted"
                } `}
                displayText={"Enter Rule ID"}
              />
              <InputField
                name={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].ruleId`}
                className={`form-control ${
                  isSubscribed ? "label-disable-color-edit-offer" : ""
                } ${
                  isFieldTouched(detailIndex, "ruleId") && !detail.ruleId
                    ? "error-input"
                    : ""
                } `}
                type="text"
                disabled={isSubscribed}
                handleChange={(e) =>
                  updateField(detailIndex, "ruleId", e.target.value)
                }
                value={detail.ruleId || ""}
                invalid={
                  isFieldTouched(detailIndex, "ruleId") && !detail.ruleId
                }
              />
            </Col>
            <Col xs={12} md={6} lg={4} xxl={3} className="mb-3">
              <Label>Name</Label>
              <LabelText
                htmlFor={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].name`}
                classLabelName={`d-block ms-0 rv-sub-label ${
                  isFieldTouched(detailIndex, "name") && !detail.name
                    ? "rv-label-danger"
                    : "rv-label-muted"
                } `}
                displayText={"Enter Name"}
              />
              <InputField
                name={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].name`}
                className={`form-control ${
                  isSubscribed ? "label-disable-color-edit-offer" : ""
                }`}
                type="text"
                disabled={isSubscribed}
                handleChange={(e) =>
                  updateField(detailIndex, "name", e.target.value)
                }
                value={detail.name || ""}
                invalid={isFieldTouched(detailIndex, "name") && !detail.name}
              />
            </Col>
            <Col xs={12} md={6} lg={4} xxl={3} className="mb-3">
              <Label>Logical Operator</Label>
              <LabelText
                htmlFor={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].logicalOperator`}
                classLabelName={`d-block ms-0 rv-sub-label ${
                  isFieldTouched(detailIndex, "logicalOperator") &&
                  !detail.logicalOperator
                    ? "rv-label-danger"
                    : "rv-label-muted"
                } `}
                displayText={"Select Logical Operator"}
              />
              <SelectField
                name={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].logicalOperator`}
                isDisabled={isSubscribed}
                options={logicalOperatorOptions}
                handleChange={(selectedOption) =>
                  updateField(detailIndex, "logicalOperator", selectedOption)
                }
                value={detail.logicalOperator || ""}
                className={
                  isFieldTouched(detailIndex, "logicalOperator") &&
                  !detail.logicalOperator
                    ? "error-input"
                    : ""
                }
                placeholder={" "}
                isRVStyle={true}
              />
            </Col>
            <Col md={3} className="mb-3">
              {/* <Label>Actions</Label> */}
              <div className="d-flex justify-content-center align-items-center mb-3">
                <div className="d-flex align-items-center ms-auto">
                  {!isSubscribed && (
                    <>
                      {detailIndex === 0 ? null : (
                        <i
                          className="ri-delete-bin-5-line text-danger fs-5"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            removeAttributeBasedDetail(detailIndex)
                          }
                          title="Delete Attribute Detail"
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          <Col className="px-3">
            <div className="position-relative mx-n3 mb-1">
              <div className="pb-2 px-3">
                <Label className="rv-form-label mb-0">Conditions</Label>
              </div>
            </div>
            <Row className="border-bottom mb-3">
              <Col lg={3} md={6}>
                {<Label>Attribute Name</Label>}
              </Col>
              <Col lg={3} md={6}>
                {<Label>Value</Label>}
              </Col>
              <Col lg={3} md={6}>
                {<Label>Operator</Label>}
              </Col>
              <Col lg={3} md={6}>
                {<Label>Actions</Label>}
              </Col>
            </Row>
            {detail.conditions && detail.conditions.length === 0 ? (
              <div className="text-center text-muted py-2">
                No Conditions Added
              </div>
            ) : (
              detail.conditions?.map((condition, conditionIndex) => (
                <Row key={conditionIndex} className="pb-4">
                  <Col lg={3} md={6}>
                    {/* {conditionIndex === 0 && <Label>Attribute Name</Label>} */}
                    <SelectField
                      name={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].conditions[${conditionIndex}].attributeName`}
                      isDisabled={isSubscribed}
                      options={attributeNameOptions}
                      handleChange={(selectedOption) => {
                        updateConditionField(
                          detailIndex,
                          conditionIndex,
                          "attributeName",
                          selectedOption
                        );
                      }}
                      value={condition?.attributeName || ""}
                      className={
                        isConditionFieldTouched(
                          detailIndex,
                          conditionIndex,
                          "attributeName"
                        ) && !condition.attributeName
                          ? "error-input"
                          : ""
                      }
                      placeholder="Select Attribute Name"
                      isRVStyle={true}
                    />
                  </Col>
                  <Col lg={3} md={6}>
                    {/* {conditionIndex === 0 && <Label>Value</Label>} */}
                    <InputField
                      name={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].conditions[${conditionIndex}].value`}
                      className={`form-control ${
                        isSubscribed ? "label-disable-color-edit-offer" : ""
                      }`}
                      type="text"
                      // disabled={true}
                      handleChange={(e) =>
                        updateConditionField(
                          detailIndex,
                          conditionIndex,
                          "value",
                          e.target.value
                        )
                      }
                      value={condition?.value || ""}
                      placeholder="Enter Value"
                      invalid={
                        isConditionFieldTouched(
                          detailIndex,
                          conditionIndex,
                          "value"
                        ) && !condition.value
                      }
                    />
                  </Col>
                  <Col lg={3} md={6}>
                    {/* {conditionIndex === 0 && <Label>Operator</Label>} */}
                    <SelectField
                      name={`recurringUsageRatePlan[${index}].attributeBasedDetails[${detailIndex}].conditions[${conditionIndex}].operator`}
                      isDisabled={isSubscribed}
                      options={conditionOperatorOptions}
                      handleChange={(selectedOption) =>
                        updateConditionField(
                          detailIndex,
                          conditionIndex,
                          "operator",
                          selectedOption
                        )
                      }
                      value={condition.operator || ""}
                      className={
                        isConditionFieldTouched(
                          detailIndex,
                          conditionIndex,
                          "operator"
                        ) && !condition.operator
                          ? "error-input"
                          : ""
                      }
                      placeholder={"Select Operator"}
                      isRVStyle={true}
                    />
                  </Col>
                  <Col lg={3} md={6}>
                    {/* {conditionIndex === 0 && <Label>Actions</Label>} */}
                    <div className="ms-2 mt-2 d-flex align-items-end">
                      {!isSubscribed && (
                        <>
                          {conditionIndex === 0 ? (
                            <>
                              <i
                                className="ri-add-circle-line text-primary fs-5 me-3"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  if (
                                    condition.attributeName &&
                                    condition.operator &&
                                    condition.value
                                  ) {
                                    addCondition(detailIndex);
                                  }
                                }}
                                title="Add Condition"
                              />
                              <Link
                                to="#"
                                className="link-danger"
                                // onClick={() => onResetRow(idx)}
                              >
                                <i
                                  data-testid="delete"
                                  className="ri-eraser-line fs-5"
                                ></i>
                              </Link>
                            </>
                          ) : (
                            <i
                              className="ri-delete-bin-5-line text-danger fs-5"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                removeCondition(detailIndex, conditionIndex)
                              }
                              title="Delete Condition"
                            />
                          )}
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              ))
            )}
          </Col>
        </div>
      ))}
    </>
  );
};

export default AttributeBasedDetailsTableRecurring;
